import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  detectLanguageFromPath,
  stripLanguagePrefix,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
} from "../utils/languageUtils";

/**
 * Locale-aware SEO component for INAQ.
 *
 * Locked decisions (see MULTILINGUAL_TRANSLATION_MASTER_PLAN.md):
 *  - Canonical host: https://inaq.sk (no www).
 *  - Self-referencing canonical per locale.
 *  - Reciprocal hreflang emitted ONLY for approved, published siblings
 *    (passed in via `approvedSiblings` prop). x-default points at Slovak.
 *  - Correct <html lang> per locale.
 *  - Locale-specific title/description/OG/Twitter/JSON-LD.
 *  - Machine-draft and preview content is excluded from indexing.
 *  - All user-derived strings are XML-escaped defensively.
 */

export const CANONICAL_HOST = "https://inaq.sk";

export const LOCALE_OG_MAP = Object.freeze({
  sk: "sk_SK",
  en: "en_US",
  pl: "pl_PL",
  cs: "cs_CZ",
});

export const LOCALE_HREFLANG_MAP = Object.freeze({
  sk: "sk",
  en: "en",
  pl: "pl",
  cs: "cs",
});

/**
 * Escape characters that would break XML text content.
 */
export const escapeXmlText = (value) => {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
};

/**
 * Escape attribute values for XML/SGML contexts.
 */
export const escapeXmlAttribute = (value) => {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
};

export const resolveImage = (image) => {
  return image?.asset?.url ?? "";
};

const portableTextToPlainText = (blocks = []) => {
  if (!Array.isArray(blocks)) return "";
  return blocks
    .map((block) => {
      if (block._type !== "block" || !Array.isArray(block.children)) {
        return "";
      }
      return block.children.map((child) => child.text || "").join("");
    })
    .join("\n\n");
};

/**
 * Normalize an approvedSiblings entry to { lang, path }.
 * Accepts:
 *   { lang: "en", path: "/blog/foo" }               // explicit
 *   { language: "en", slug: "/blog/foo" }           // alt shape
 *   "en:/blog/foo"                                   // string shorthand
 *   null/undefined                                   // dropped
 */
const normalizeSibling = (entry) => {
  if (!entry) return null;
  if (typeof entry === "string") {
    const idx = entry.indexOf(":");
    if (idx <= 0) return null;
    const lang = entry.slice(0, idx).toLowerCase();
    const path = entry.slice(idx + 1) || "/";
    if (!SUPPORTED_LANGUAGES.includes(lang)) return null;
    return { lang, path: path.startsWith("/") ? path : `/${path}` };
  }
  const lang = (entry.lang || entry.language || "").toLowerCase();
  if (!SUPPORTED_LANGUAGES.includes(lang)) return null;
  const raw =
    entry.path ||
    entry.url ||
    (entry.slug
      ? entry.slug.startsWith("/")
        ? entry.slug
        : `/${entry.slug}`
      : null);
  if (!raw) return null;
  let path = raw;
  try {
    if (/^https?:\/\//i.test(path)) {
      const u = new URL(path);
      path = u.pathname + (u.search || "");
    }
  } catch (_e) {
    // keep raw
  }
  return { lang, path: path.startsWith("/") ? path : `/${path}` };
};

/**
 * Build a canonical URL from a locale + path.
 *  - Slovak (default) lives at the host root: https://inaq.sk/blog/foo
 *  - Other locales use a prefix segment: https://inaq.sk/en/blog/foo
 */
export const buildCanonicalUrl = (lang, path = "/") => {
  const cleanPath = path && path.startsWith("/") ? path : `/${path || ""}`;
  if (!lang || lang === DEFAULT_LANGUAGE) {
    return cleanPath === "/" || cleanPath === ""
      ? `${CANONICAL_HOST}/`
      : `${CANONICAL_HOST}${cleanPath}`;
  }
  return `${CANONICAL_HOST}/${lang}${cleanPath === "/" ? "" : cleanPath}`;
};

const SEO = ({
  seo,
  slug,
  defaultTitle = "INAQ",
  defaultDescription = "",
  defaultOgImage = "",
  structuredData = null,
  faqItems = null,
  // Locale-aware props (all optional, gated):
  // - indexable: pass false to emit noindex,nofollow (machine drafts / previews)
  // - approvedSiblings: array of sibling entries to emit hreflang for. If
  //   omitted, no alternate links are emitted at all (gate closed).
  indexable = true,
  approvedSiblings = null,
  // - forceLang: override detected locale (useful for static prerender)
  forceLang = null,
}) => {
  const location = useLocation();
  const detectedLang = detectLanguageFromPath(location.pathname);
  const currentLang = forceLang || detectedLang;

  const pathInDefaultLocale = useMemo(
    () => stripLanguagePrefix(location.pathname || "/"),
    [location.pathname]
  );

  const canonicalPath = useMemo(() => {
    if (!slug && pathInDefaultLocale === "/") return "/";
    if (slug) {
      return slug.startsWith("/") ? slug : `/${slug}`;
    }
    return pathInDefaultLocale || "/";
  }, [slug, pathInDefaultLocale]);

  const canonicalUrl = buildCanonicalUrl(currentLang, canonicalPath);

  const hreflangEntries = useMemo(() => {
    const out = [];
    const list = Array.isArray(approvedSiblings) ? approvedSiblings : [];
    const seen = new Set();
    for (const raw of list) {
      const sib = normalizeSibling(raw);
      if (!sib) continue;
      const code = LOCALE_HREFLANG_MAP[sib.lang];
      if (!code || seen.has(code)) continue;
      seen.add(code);
      out.push({
        hreflang: code,
        href: buildCanonicalUrl(sib.lang, sib.path),
      });
    }
    return out;
  }, [approvedSiblings]);

  const title = seo?.metaTitle || defaultTitle;
  const description = seo?.metaDescription || defaultDescription;
  const keywords = Array.isArray(seo?.seoKeywords)
    ? seo.seoKeywords.join(", ")
    : "";
  const nofollow = seo?.nofollowAttributes || !indexable;

  const ogTitle = seo?.openGraph?.title || title;
  const ogDescription = seo?.openGraph?.description || description;
  const ogSiteName = seo?.openGraph?.siteName || "INAQ";
  const ogUrl = seo?.openGraph?.url || canonicalUrl;
  const ogImage = resolveImage(seo?.openGraph?.image) || defaultOgImage;
  const ogLocale = LOCALE_OG_MAP[currentLang] || LOCALE_OG_MAP.sk;
  const ogLocaleAlternates = SUPPORTED_LANGUAGES.filter(
    (l) => l !== currentLang
  ).map((l) => LOCALE_OG_MAP[l]);

  const twitterSite = seo?.twitter?.site || "";
  const twitterCreator = seo?.twitter?.creator || "";
  const twitterCardType = seo?.twitter?.cardType || "summary_large_image";

  const isBlogPost =
    canonicalPath.startsWith("/blog/") && canonicalPath !== "/blog/";

  const finalIndexable = indexable !== false && !nofollow;

  const organizationSchema = {
    "@type": "Organization",
    name: "INAQ",
    url: CANONICAL_HOST,
    logo: {
      "@type": "ImageObject",
      url: `${CANONICAL_HOST}/assets/img/webp/inaq_logo_web.webp`,
    },
    description:
      "INAQ je marketingová agentúra so špecializáciou na B2B marketing.",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Žilina",
      addressCountry: "SK",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+421-41-230-4414",
      contactType: "Obchodné oddelenie",
    },
    sameAs: ["https://www.facebook.com/INAQSK"],
  };

  const blogPostSchema = isBlogPost
    ? structuredData || {
        "@type": "BlogPosting",
        headline: title,
        image: ogImage
          ? [ogImage]
          : [`${CANONICAL_HOST}/assets/img/webp/inaq_blog_logo_web.webp`],
        datePublished: new Date().toISOString(),
        dateModified: new Date().toISOString(),
        author: { "@type": "Person", name: "INAQ" },
        publisher: {
          "@type": "Organization",
          name: "INAQ",
          logo: {
            "@type": "ImageObject",
            url: `${CANONICAL_HOST}/assets/img/webp/inaq_logo_web.webp`,
          },
        },
        keywords,
        description,
        url: canonicalUrl,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": canonicalUrl,
        },
        inLanguage: currentLang,
      }
    : null;

  const faqSchema =
    Array.isArray(faqItems) && faqItems.length > 0
      ? {
          "@type": "FAQPage",
          mainEntity: faqItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text:
                typeof item.answer === "string"
                  ? item.answer
                  : Array.isArray(item.answer)
                    ? portableTextToPlainText(item.answer)
                    : "Please provide answer as a string",
            },
          })),
        }
      : null;

  const schemaEntities = [organizationSchema];
  if (blogPostSchema) schemaEntities.push(blogPostSchema);
  if (faqSchema) schemaEntities.push(faqSchema);

  const finalStructuredData =
    schemaEntities.length > 1
      ? {
          "@context": "https://schema.org",
          "@graph": schemaEntities,
        }
      : {
          "@context": "https://schema.org",
          ...organizationSchema,
        };

  const pageTitle = isBlogPost ? `${title} | INAQ Blog` : title;

  if (typeof window !== "undefined" && window.__PRERENDERED) {
    return null;
  }

  return (
    <Helmet prioritizeSeoTags>
      <html lang={currentLang} />
      <title>{pageTitle}</title>

      <meta name='description' content={description} />
      {keywords && <meta name='keywords' content={keywords} />}

      <link rel='canonical' href={canonicalUrl} />

      {/* Hreflang: only for approved siblings. x-default always points at
          the Slovak (default) URL of the same page. */}
      {hreflangEntries.map((entry) => (
        <link
          key={`hreflang-${entry.hreflang}`}
          rel='alternate'
          hrefLang={entry.hreflang}
          href={entry.href}
        />
      ))}
      <link
        rel='alternate'
        hrefLang='x-default'
        href={buildCanonicalUrl(DEFAULT_LANGUAGE, canonicalPath)}
      />

      {/* Robots: noindex,nofollow when not indexable (machine drafts,
          previews). */}
      {finalIndexable ? (
        <meta
          name='robots'
          content='index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
        />
      ) : (
        <meta name='robots' content='noindex, nofollow' />
      )}

      <meta property='og:title' content={ogTitle} />
      <meta property='og:description' content={ogDescription} />
      <meta property='og:type' content={isBlogPost ? "article" : "website"} />
      <meta property='og:url' content={ogUrl} />
      {ogImage && <meta property='og:image' content={ogImage} />}
      <meta property='og:site_name' content={ogSiteName} />
      <meta property='og:locale' content={ogLocale} />
      {ogLocaleAlternates.map((alt) => (
        <meta key={alt} property='og:locale:alternate' content={alt} />
      ))}

      {isBlogPost && (
        <>
          <meta
            property='article:published_time'
            content={structuredData?.datePublished || new Date().toISOString()}
          />
          <meta
            property='article:modified_time'
            content={structuredData?.dateModified || new Date().toISOString()}
          />
          {keywords && <meta property='article:tag' content={keywords} />}
          <meta
            property='article:publisher'
            content='https://www.facebook.com/INAQSK'
          />
        </>
      )}

      <meta name='twitter:card' content={twitterCardType} />
      {twitterSite && <meta name='twitter:site' content={twitterSite} />}
      {twitterCreator && (
        <meta name='twitter:creator' content={twitterCreator} />
      )}
      <meta name='twitter:title' content={ogTitle} />
      <meta name='twitter:description' content={ogDescription} />
      {ogImage && <meta name='twitter:image' content={ogImage} />}

      {Array.isArray(seo?.additionalMetaTags) &&
        seo.additionalMetaTags.flatMap((tag, index) =>
          Array.isArray(tag.metaAttributes)
            ? tag.metaAttributes.map((attr, attrIndex) => {
                const key = attr.attributeKey;
                const value =
                  attr.attributeType === "image"
                    ? resolveImage(attr.attributeValueImage)
                    : attr.attributeValueString;
                if (!key || !value) return null;
                return (
                  <meta
                    key={`${index}-${attrIndex}`}
                    name={key}
                    content={value}
                  />
                );
              })
            : []
        )}

      <script type='application/ld+json'>
        {JSON.stringify(finalStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
